import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import { getHighlightsForProductLineV2, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import ProductInfoContainer from '../components/ProductInfoContainer';
import BannerSection from '../components/banners/BannerSection';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import SuiteFeatureTable from '../components/feature-table/suite-feature-table/SuiteFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { SuitePageCardContainer } from '../components/merch-card/card-containers/SuitePageCardContainer';
import { SUITE_PAGE, SUITE_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT, PRICING_AND_PURCHASING_FAILED_STATUS } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SuitePage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('crmSuite');
  const screen = SCREENS.SUITE;
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const {
    tab
  } = usePageParams();
  useEffect(() => {
    trackPageView({
      screen,
      tab
    });
  }, [screen, tab]);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const marketingHighlightedProducts = getHighlightsForProductLineV2(ProductLines.MARKETING);
  const filteredMarketingHighlightedProducts = marketingHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const salesHighlightedProducts = getHighlightsForProductLineV2(ProductLines.SALES);
  const filteredSalesHighlightedProducts = salesHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const serviceHighlightedProducts = getHighlightsForProductLineV2(ProductLines.SERVICE);
  const filteredServiceHighlightedProducts = serviceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const operationsHighlightedProducts = getHighlightsForProductLineV2(ProductLines.OPERATIONS);
  const filteredOperationsHighlightedProducts = operationsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const cmsHighlightedProducts = getHighlightsForProductLineV2(ProductLines.CMS);
  const filteredCmsHighlightedProducts = cmsHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const commerceHighlightedProducts = getHighlightsForProductLineV2(ProductLines.COMMERCE);
  const filteredCommerceHighlightedProducts = commerceHighlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const productsByProductLine = {
    [ProductLines.MARKETING]: filteredMarketingHighlightedProducts,
    [ProductLines.SALES]: filteredSalesHighlightedProducts,
    [ProductLines.SERVICE]: filteredServiceHighlightedProducts,
    [ProductLines.OPERATIONS]: filteredOperationsHighlightedProducts,
    [ProductLines.CMS]: filteredCmsHighlightedProducts,
    [ProductLines.COMMERCE]: filteredCommerceHighlightedProducts
  };
  if (hasPricingAndPurchasingFailed) {
    return /*#__PURE__*/_jsx(ErrorPage, {
      errorCause: PRICING_AND_PURCHASING_FAILED_STATUS
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: NavItemEnum.SUITE
    }),
    errorBoundaryName: SUITE_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "SUITE_PAGE"
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: NavItemEnum.SUITE,
        children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
          children: [/*#__PURE__*/_jsx(BannerSection, {
            screen: screen
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              nonProductIdentifier: NavItemEnum.SUITE
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              nonProductIdentifier: NavItemEnum.SUITE
            })]
          }), /*#__PURE__*/_jsx(SuitePageCardContainer, {
            selectedTab: tab
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(CreditsCallout, {
              navItem: NavItemEnum.SUITE
            })
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(SuiteFeatureTable, {
              productsByProductLine: productsByProductLine,
              screen: screen
            }, tab)
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(Faq, {
            children: FaqsConfig[NavItemEnum.SUITE].map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            })
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: "crmsuite-footer",
            screen: screen,
            apiName: "crm-suite-starter-bundle"
          })]
        })
      })]
    })
  });
};
export default withErrorBoundary(SuitePage, {
  errorBoundaryName: SUITE_PAGE,
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  severity: ERROR_SEVERITY.HIGH
});